<template>
    <EditComponent
        dialog-id="editIndustryExpertiseDialog"
        open-dialog-event="industry-expertise:edit-mode"
        updated-event="industry-expertise:updated"
        :confirm-function="updateExpertise"
        :emit-close-function="emitCloseEvent"
        :confirm-enabled="isValidToSubmit"
        :track-section-opened="EditionOpenedSection.INDUSTRY_EXPERTISE"
        :track-existing-element="true"
    >
        <template #edit-header>
            <p class="edit-title">
                {{ t('profile.edition.completion.industry.expertise.title') }}
            </p>
            <p class="edit-subtitle">{{ t('profile.edition.completion.industry.expertise.subtitle', {0: maxSelection}) }}</p>
        </template>
        <template #edit-body>
            <IndustryExpertiseSelection
                :all-expertises="availableExpertise"
                :selected-expertises="selectedExpertises"
                :max-selection="maxSelection"
                @industry-expertise:updated="handleExpertisesChanged"
                @industry-expertise:max-selection-reached="handleMaxSelectionReached"
            />
        </template>
    </EditComponent>
</template>

<script setup lang="ts">
    import {reactive, computed, ref, onBeforeMount, onMounted, onBeforeUnmount} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useTranslation} from '#imports';
    import {pushVJoySnackbar} from '@maltjoy/core-vue';
    import EditComponent from '~/components/edit/EditComponent.vue';
    import IndustryExpertiseSelection from '~/components/edit/skills/IndustryExpertiseSelection.vue';
    import {EditionOpenedSection} from '~/config/TrackingEvents';
    import {useProfileStore} from '~/store/profileStore';
    import type {IndustryExpertiseItem} from '~/types/domain/skill/Industry';
    import type {MinMaxRule} from '~/types/domain/ValidationRule';
    import {eventBus} from '@/config/events';

    const {t} = useTranslation();

    const {listAllIndustryExpertise, updateIndustryExpertise, getIndustryExpertiseRules} = useProfileStore();

    const {industryExpertises} = storeToRefs(useProfileStore());

    const availableExpertise = reactive<IndustryExpertiseItem[]>([]);
    const selectedExpertises = ref<IndustryExpertiseItem[]>(industryExpertises.value!);
    const expertiseRules = ref<MinMaxRule>();
    const isValidToSubmit = computed(() => (expertiseRules.value ? expertiseRules.value.isValid(selectedExpertises.value.length) : false));
    const maxSelection = computed(() => (expertiseRules.value ? expertiseRules.value.max : 5));

    onMounted(async () => {
        const expertiseList = await listAllIndustryExpertise();
        if (expertiseList) {
            availableExpertise.push(...expertiseList.all);
            availableExpertise.sort((a, b) => a.label.localeCompare(b.label));
        }
        expertiseRules.value = await getIndustryExpertiseRules();
    });

    function handleExpertisesChanged(expertises: IndustryExpertiseItem[]) {
        selectedExpertises.value = expertises;
    }

    function handleMaxSelectionReached() {
        pushVJoySnackbar({
            props: {
                message: t('profile.creation.completion.wizard.industry.expertise.error.limit', {0: expertiseRules.value!.max}),
                level: 'error',
            },
        });
    }

    async function updateExpertise() {
        const expertiseRequests: string[] = selectedExpertises.value.map((item) => item.id);
        await updateIndustryExpertise({expertises: expertiseRequests});
        eventBus.emit('industry-expertise:updated');
        reset();
    }

    function emitCloseEvent() {
        reset();
    }

    function reset() {
        selectedExpertises.value = industryExpertises.value!;
    }
</script>

<style lang="scss" scoped>
    .edit-title {
        display: flex;
        align-items: center;
        gap: var(--joy-core-spacing-2);
        margin-bottom: var(--joy-core-spacing-2);
        & span {
            cursor: default;
        }
    }

    .edit-subtitle {
        font-weight: var(--joy-font-weight-normal);
        font-size: var(--joy-font-size-primary-400);
        margin-bottom: var(--joy-core-spacing-1);
    }
</style>
